/**
 * Label Document Model (LDM): the neutral representation the ZPL parser
 * produces and the canvas renderer consumes. All coordinates are printer dots.
 *
 * Ported from ns-web resources/js/components/common/label-viewer/utils/label-document-model.js
 * Changes: TypeScript discriminated union; EPL-only fields dropped; barcode
 * options typed per symbology; text fields carry their origin kind (^FO / ^FT).
 */

export type Rotation = 0 | 90 | 180 | 270;

export type BarcodeType =
  | 'code128'
  | 'code39'
  | 'code93'
  | 'ean13'
  | 'ean8'
  | 'upca'
  | 'upce'
  | 'itf'
  | 'codabar'
  | 'logmars'
  | 'msi'
  | 'plessey'
  | 'gs1databar'
  | 'postnet'
  | 'planetcode'
  | 'industrial25'
  | 'standard25'
  | 'codablockf'
  | 'qrcode'
  | 'datamatrix'
  | 'pdf417'
  | 'micropdf417'
  | 'aztec'
  | 'maxicode'
  | 'code49';

export interface FontSpec {
  /** ZPL font letter/digit, upper-cased ('0', 'A'…'Z'). */
  name: string;
  /** Character height in dots (0 = printer default). */
  height: number;
  /** Character width in dots (0 = proportional to height). */
  width: number;
}

export interface FieldBlock {
  /** ^FB a: block width in dots. */
  maxWidth: number;
  /** ^FB b: maximum number of lines. Extra text overprints the last line. */
  maxLines: number;
  /** ^FB c: extra dots between lines (added to the font height). */
  lineSpacing: number;
  /** ^FB d: justification. */
  align: 'L' | 'C' | 'R' | 'J';
  /** ^FB e: hanging indent in dots for lines after the first. */
  hangingIndent: number;
}

export interface TextElement {
  type: 'text';
  x: number;
  /** Top of the first line (^FT origins are converted from the baseline). */
  y: number;
  rotate: Rotation;
  font: FontSpec;
  text: string;
  /** ^FR: field reverse (draws white on black where it overlaps black). */
  reverse: boolean;
  block: FieldBlock | null;
}

export interface QrOptions {
  model: number;
  /** Dots per module (^BQ magnification). */
  magnification: number;
  /** Effective error-correction level. The ^FD prefix wins over the ^BQ parameter. */
  errorCorrection: 'L' | 'M' | 'Q' | 'H';
}

export interface LinearOptions {
  /** Narrow bar (module) width in dots, from ^BY. */
  moduleWidth: number;
  /** Wide/narrow ratio, from ^BY (only used by two-width symbologies). */
  ratio: number;
  /** ^BC mode: N (none), U (UCC case), A (automatic), D (UCC/EAN). */
  mode?: 'N' | 'U' | 'A' | 'D';
  /** Print the human-readable line above instead of below. */
  textAbove?: boolean;
}

export interface MatrixOptions {
  magnification: number;
  columns: number;
  rows: number;
  eclevel?: number;
}

export interface BarcodeElement {
  type: 'barcode';
  /** The field's ^FO / ^FT position (label home applied), before the ^FT and ^BQ offsets. */
  origin: { x: number; y: number };
  x: number;
  /** Top of the symbol (^FT origins are converted from the bottom edge). */
  y: number;
  /** Bar height in dots (1D and stacked symbologies). */
  h: number;
  rotate: Rotation;
  barcodeType: BarcodeType;
  /** Encoded payload, without ZPL-only prefixes such as the QR "QA,". */
  data: string;
  /** Human-readable interpretation line. */
  showText: boolean;
  options: QrOptions | LinearOptions | MatrixOptions;
}

export interface BoxElement {
  type: 'box';
  x: number;
  y: number;
  w: number;
  h: number;
  thickness: number;
  color: 'B' | 'W';
  /** ^GB rounding 0–8. Corner radius = rounding / 8 × (shorter side / 2). */
  rounding: number;
  reverse: boolean;
}

export interface CircleElement {
  type: 'circle';
  x: number;
  y: number;
  diameter: number;
  thickness: number;
  color: 'B' | 'W';
  reverse: boolean;
}

export interface DiagonalElement {
  type: 'diagonal';
  x: number;
  y: number;
  w: number;
  h: number;
  thickness: number;
  color: 'B' | 'W';
  /** L = leans left (top-right to bottom-left), R = leans right. */
  orientation: 'L' | 'R';
  reverse: boolean;
}

export interface GraphicElement {
  type: 'graphic';
  x: number;
  y: number;
  /** Bytes per row of the 1-bit bitmap. */
  rowBytes: number;
  /** Decoded bitmap bytes, MSB first, 1 = black. */
  bytes: Uint8Array;
  reverse: boolean;
}

export type LabelElement =
  | TextElement
  | BarcodeElement
  | BoxElement
  | CircleElement
  | DiagonalElement
  | GraphicElement;

export interface LabelMeta {
  /** ^PW print width in dots, if declared. */
  widthDots: number | null;
  /** ^LL label length in dots, if declared. */
  heightDots: number | null;
  /** ^PO I rotates the whole label 180°. */
  rotate: 0 | 180;
  /** ^LH label home, already applied to element coordinates. */
  labelHome: { x: number; y: number };
  /** ^LR Y: reverse the whole label. */
  reverse: boolean;
  /** ^CI value in effect at the end of the label (28 = UTF-8). */
  encoding: number | null;
  /** ^PQ quantity, if declared. */
  quantity: number | null;
}

export interface LabelDoc {
  meta: LabelMeta;
  elements: LabelElement[];
}

export function createLabelDoc(meta: Partial<LabelMeta> = {}): LabelDoc {
  return {
    meta: {
      widthDots: meta.widthDots ?? null,
      heightDots: meta.heightDots ?? null,
      rotate: meta.rotate ?? 0,
      labelHome: meta.labelHome ?? { x: 0, y: 0 },
      reverse: meta.reverse ?? false,
      encoding: meta.encoding ?? null,
      quantity: meta.quantity ?? null,
    },
    elements: [],
  };
}
